/**
 * Shipments Redux Slice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Shipment, ShipmentStats, ShipmentsFilter, PaginationMeta, PaginatedShipments } from '../domain/types'
import { DEFAULT_PAGE_SIZE } from '../domain/constants'

export interface ShipmentsState {
  items: Shipment[]
  selectedShipment: Shipment | null
  stats: ShipmentStats
  filter: ShipmentsFilter
  pagination: PaginationMeta
  loading: boolean
  error: string | null
}

const initialState: ShipmentsState = {
  items: [],
  selectedShipment: null,
  stats: { scheduled: 0, loading: 0, delivered: 0, total: 0 },
  filter: { status: 'all', page: 1, pageSize: DEFAULT_PAGE_SIZE },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE, pageCount: 1, total: 0 },
  loading: false,
  error: null,
}

export const shipmentsSlice = createSlice({
  name: 'shipments',
  initialState,
  reducers: {
    // Fetch actions
    fetchShipments: (state, _action: PayloadAction<ShipmentsFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchShipmentsSuccess: (state, action: PayloadAction<PaginatedShipments>) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
      state.error = null
    },
    fetchShipmentsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Stats actions
    fetchShipmentStats: () => {
      // Don't set loading for stats (background operation)
    },
    fetchShipmentStatsSuccess: (state, action: PayloadAction<ShipmentStats>) => {
      state.stats = action.payload
    },
    fetchShipmentStatsFailure: () => {
      // Silent fail for stats
    },

    // Legacy setters (keep for compatibility)
    setShipments: (state, action: PayloadAction<Shipment[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setSelectedShipment: (state, action: PayloadAction<Shipment | null>) => {
      state.selectedShipment = action.payload
    },
    setStats: (state, action: PayloadAction<ShipmentStats>) => {
      state.stats = action.payload
    },
    setFilter: (state, action: PayloadAction<ShipmentsFilter>) => {
      state.filter = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Filter actions
    searchShipments: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload }
    },
    filterByStatus: (state, action: PayloadAction<ShipmentsFilter['status']>) => {
      state.filter = { ...state.filter, status: action.payload }
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, page: action.payload }
      state.pagination.page = action.payload // Update pagination immediately
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, pageSize: action.payload, page: 1 }
      state.pagination.pageSize = action.payload // Update pagination immediately
      state.pagination.page = 1
    },
    goToNextPage: (state) => {
      const currentPage = state.filter.page ?? 1
      if (currentPage < state.pagination.pageCount) {
        state.filter = { ...state.filter, page: currentPage + 1 }
      }
    },
    goToPreviousPage: (state) => {
      const currentPage = state.filter.page ?? 1
      if (currentPage > 1) {
        state.filter = { ...state.filter, page: currentPage - 1 }
      }
    },

    clearShipments: (state) => {
      state.items = []
      state.selectedShipment = null
      state.error = null
    },
  },
})

export const shipmentsActions = shipmentsSlice.actions
export const shipmentsReducer = shipmentsSlice.reducer
